import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MInputField from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useProfileField } from "@/hooks/useHealthProfile";
import { getMemberInfo } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { MedicalDetailType } from "@/types/health-profile";
import { trackUserDataLoaded } from "@/utils/gtm";
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from "@/constants/analytics.constants";

interface ISurgery {
  name: string;
  date: string;
}

const SurgeriesForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { fieldValue: surgeries = [], saveField: saveSurgeries } =
    useProfileField<ISurgery[]>(memberId, MedicalDetailType.SURGERIES, []);

  // For edit mode: /SurgeriesForm?mode=edit&index=0
  const editingSurgery = location.state?.surgery;
  const index = location.state?.idx;
  const [surgeryName, setSurgeryName] = useState(editingSurgery?.name || "");
  const [surgeryDate, setSurgeryDate] = useState(editingSurgery?.date || "");
  const [loading, setLoading] = useState(false);

  // Track initial values for dirty check in edit mode
  const [initialSurgeryName, setInitialSurgeryName] = useState(
    editingSurgery?.name || ""
  );
  const [initialSurgeryDate, setInitialSurgeryDate] = useState(
    editingSurgery?.date || ""
  );

  // Ensure fields are prefilled when editing surgery changes (for edit mode)
  useEffect(() => {
    if (editingSurgery) {
      setSurgeryName(editingSurgery.name || "");
      setSurgeryDate(editingSurgery.date || "");
      setInitialSurgeryName(editingSurgery.name || "");
      setInitialSurgeryDate(editingSurgery.date || "");
    }
  }, [editingSurgery]);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_ADD,
    });
  }, []);

  const sort = (surgeries: ISurgery[]) => {
    return surgeries.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const handleSave = async () => {
    const trimmed = surgeryName.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      if (editingSurgery && index !== undefined) {
        // Edit
        const updatedSurgery = { name: trimmed, date: surgeryDate };
        const updatedSurgeries = [...surgeries];
        updatedSurgeries[index] = updatedSurgery;
        await saveSurgeries(sort(updatedSurgeries));
      } else {
        // Add
        const newSurgery = { name: trimmed, date: surgeryDate };
        const updatedSurgeries = [...surgeries, newSurgery];
        await saveSurgeries(sort(updatedSurgeries));
      }
      
      // Track completion
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_ADD_COMPLETE,
      });

      navigate("/profile/health-profile/surgeries");
    } catch (error) {
      showToast({ message: t("errors.toast"), variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const checkValid = () => {
    const trimmedName = surgeryName.trim();
    if (!trimmedName) return false;
    const trimmedDate = surgeryDate.trim();
    if (!trimmedDate) return false;
    if (editingSurgery &&
    (trimmedName === initialSurgeryName && trimmedDate === initialSurgeryDate)) return false;
    return true;
  };

  return (
    <div className="profile-page-root">
      <FormHeader module="add_surgeries" memberInfo={memberInfo} />
      <MSpacer />
      <div className="profile-input-container">
        <MInputField
          label={t("add_surgeries.surgeryName")}
          value={surgeryName}
          placeholder={t("common.notAnswered")}
          onChange={(e) => setSurgeryName(e.target.value)}
        />
        <MDateInputField
          showYearOnly
          label={t("add_surgeries.surgeryDate")}
          minYear={memberInfo.dobYear || 1940}
          value={surgeryDate ? new Date(surgeryDate) : undefined}
          onDateChange={(date) => setSurgeryDate(date.toISOString())}
          formatDate={(date) => (date ? date.getFullYear().toString() : "")}
          placeholder={t("common.notAnswered")}
          size="md"
          datePickerTitle={t("common.selectYear")}
        />
      </div>
      <div className="confirm-footer">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={!checkValid()}
          onClick={handleSave}
          text={t("common.done")}
        />
      </div>
    </div>
  );
};

export default SurgeriesForm;
